import { useCallback, useReducer } from 'react';
import { TipShareShift } from '../../types';

// A discriminated-union state machine for the shift drawer/dialog.
// Replaces 5 separate useState calls + useDialog ensuring
// all related state transitions happen in a single dispatch (single re-render).
type ShiftMode = 'idle' | 'create' | 'view' | 'edit' | 'delete';

interface ShiftDrawerState {
  mode: ShiftMode;
  selectedShift: TipShareShift | undefined;
}

type ShiftDrawerAction =
  | { type: 'OPEN_CREATE' }
  | { type: 'OPEN_VIEW'; shift: TipShareShift }
  | { type: 'OPEN_EDIT'; shift: TipShareShift }
  | { type: 'ENTER_EDIT_MODE' }
  | { type: 'OPEN_DELETE'; shift: TipShareShift }
  | { type: 'CLOSE' };

const initialState: ShiftDrawerState = { mode: 'idle', selectedShift: undefined };

function shiftDrawerReducer(state: ShiftDrawerState, action: ShiftDrawerAction): ShiftDrawerState {
  switch (action.type) {
    case 'OPEN_CREATE':
      return { mode: 'create', selectedShift: undefined };
    case 'OPEN_VIEW':
      return { mode: 'view', selectedShift: action.shift };
    case 'OPEN_EDIT':
      return { mode: 'edit', selectedShift: action.shift };
    case 'ENTER_EDIT_MODE':
      return { ...state, mode: 'edit' };
    case 'OPEN_DELETE':
      return { mode: 'delete', selectedShift: action.shift };
    case 'CLOSE':
      return initialState;
    default:
      return state;
  }
}

export default function useShiftDrawerState() {
  const [state, dispatch] = useReducer(shiftDrawerReducer, initialState);

  // Derived booleans — no extra state, computed from the single mode field
  const isDrawerOpen = state.mode === 'create' || state.mode === 'view' || state.mode === 'edit';
  const isDeleteDialogOpen = state.mode === 'delete';
  const isEditing = state.mode === 'edit';
  const isViewOnly = state.mode === 'view';

  const openCreate = useCallback(() => dispatch({ type: 'OPEN_CREATE' }), []);
  const openView = useCallback((shift: TipShareShift) => dispatch({ type: 'OPEN_VIEW', shift }), []);
  const openEdit = useCallback((shift: TipShareShift) => dispatch({ type: 'OPEN_EDIT', shift }), []);
  const enterEditMode = useCallback(() => dispatch({ type: 'ENTER_EDIT_MODE' }), []);
  const openDelete = useCallback((shift: TipShareShift) => dispatch({ type: 'OPEN_DELETE', shift }), []);
  const close = useCallback(() => dispatch({ type: 'CLOSE' }), []);

  return {
    selectedShift: state.selectedShift,
    isDrawerOpen,
    isDeleteDialogOpen,
    isEditing,
    isViewOnly,
    openCreate,
    openView,
    openEdit,
    enterEditMode,
    openDelete,
    close,
  };
}
